import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './component/ui/ProtectedRoute';
import DashboardLayout from './component/Layout/DashboardLayout';
import PageLoader from './component/ui/PageLoader';
import { getRoleHomePath } from './config/navigation';

// ── Existing pages ─────────────────────────────────────────────────────────────
const Login             = lazy(() => import('./pages/Login'));
const ForgotPassword    = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword     = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail       = lazy(() => import('./pages/VerifyEmail'));
const AcceptInvite      = lazy(() => import('./pages/AcceptInvite'));
const RequestAccess     = lazy(() => import('./pages/RequestAccess'));
const RequestAccessVerify = lazy(() => import('./pages/RequestAccessVerify'));
// ── Public marketplace (item 7) — anonymous-first, own shell ───────────────
const Listings          = lazy(() => import('./pages/public/Listings'));
const ListingDetail     = lazy(() => import('./pages/public/ListingDetail'));
const ApplicationVerify = lazy(() => import('./pages/public/ApplicationVerify'));
const Enquiries         = lazy(() => import('./pages/Enquiries'));
const EnquiryDetail     = lazy(() => import('./pages/EnquiryDetail'));
const Dashboard         = lazy(() => import('./pages/Dashboard'));
const Properties        = lazy(() => import('./pages/Properties'));
const PropertyDetail    = lazy(() => import('./pages/PropertyDetail'));
const Units             = lazy(() => import('./pages/Units'));
const UnitDetail        = lazy(() => import('./pages/UnitDetail'));
const Tenants           = lazy(() => import('./pages/Tenants'));
const TenantDetail      = lazy(() => import('./pages/TenantDetail'));
const Staff             = lazy(() => import('./pages/Staff'));
const StaffDetail       = lazy(() => import('./pages/StaffDetail'));
const PlatformUserDetail = lazy(() => import('./pages/PlatformUserDetail'));
const ImportData        = lazy(() => import('./pages/ImportData'));
const Leases            = lazy(() => import('./pages/Leases'));
const LeaseDetail       = lazy(() => import('./pages/LeaseDetail'));
const Applications      = lazy(() => import('./pages/Applications'));
const ApplicationDetail = lazy(() => import('./pages/ApplicationDetail'));
const Invoices          = lazy(() => import('./pages/Invoices'));
const Payments          = lazy(() => import('./pages/Payments'));
const MpesaLogs         = lazy(() => import('./pages/MpesaLogs'));
const Maintenance       = lazy(() => import('./pages/Maintenance'));
const Expenses          = lazy(() => import('./pages/Expenses'));
const Vendors           = lazy(() => import('./pages/Vendors'));
const Inspections       = lazy(() => import('./pages/Inspections'));
const Analytics         = lazy(() => import('./pages/Analytics'));
const RentalIncomeReport = lazy(() => import('./pages/RentalIncomeReport'));  // KRA-1
const ServiceChargeStatement = lazy(() => import('./pages/ServiceChargeStatement'));  // v1.4.1
const VisitorLog = lazy(() => import('./pages/VisitorLog'));                          // v1.4.1
const MyVisitors = lazy(() => import('./pages/MyVisitors'));                          // v1.4.1
const Settings          = lazy(() => import('./pages/Settings'));
const Messages          = lazy(() => import('./pages/Messages'));
const UserManagement    = lazy(() => import('./pages/UserManagement'));
const PlatformDashboard = lazy(() => import('./pages/PlatformDashboard'));
const Prospects         = lazy(() => import('./pages/Prospects'));
// ── Platform-operator console (v1.2 Block 3 S1g) — the superadmin shell lives
//    under /platform/*; the old superadmin paths redirect there below.
const Organisations      = lazy(() => import('./pages/platform/Organisations'));
const OrganisationDetail = lazy(() => import('./pages/platform/OrganisationDetail'));
const PlatformRevenue    = lazy(() => import('./pages/platform/PlatformRevenue'));
const PaymentsHealth     = lazy(() => import('./pages/platform/PaymentsHealth'));
const PlatformSystem     = lazy(() => import('./pages/platform/PlatformSystem'));
const PlatformAudit      = lazy(() => import('./pages/platform/PlatformAudit'));
const ComplianceConfig   = lazy(() => import('./pages/platform/ComplianceConfig'));   // KRA-1
const Plans              = lazy(() => import('./pages/platform/Plans'));              // v1.4.1
const Collections       = lazy(() => import('./pages/Collections'));
// v1.4 B5: /deposits is the drill-down-aware Deposits page on the ledger
// (it rendered the older SecurityDeposits.jsx, which ignored the URL filters).
const Deposits          = lazy(() => import('./pages/Deposits'));
const Utilities         = lazy(() => import('./pages/Utilities'));
const Home              = lazy(() => import('./pages/Home'));   // v1.4 TH-1
const TenantNotices     = lazy(() => import('./pages/TenantNotices'));
const MyDeposit         = lazy(() => import('./pages/MyDeposit'));   // v1.4 TH-3
const RentRecords       = lazy(() => import('./pages/RentRecords'));   // v1.4 TH-2
const VerifyRecord      = lazy(() => import('./pages/public/VerifyRecord'));
const MyLease           = lazy(() => import('./pages/MyLease'));
const PaymentHistory    = lazy(() => import('./pages/PaymentHistory'));
const Receipts          = lazy(() => import('./pages/Receipts'));
const ReportIssue       = lazy(() => import('./pages/ReportIssue'));
const MyMaintenance     = lazy(() => import('./pages/MyMaintenance'));
const MyMaintenanceDetail = lazy(() => import('./pages/MyMaintenanceDetail'));
const NoticeToVacate    = lazy(() => import('./pages/NoticeToVacate'));
const MyTasks           = lazy(() => import('./pages/MyTasks'));

// ── New pages ──────────────────────────────────────────────────────────────────
const VacateNotices     = lazy(() => import('./pages/VacateNotices'));
const VacancyListings   = lazy(() => import('./pages/VacancyListings'));
const Announcements     = lazy(() => import('./pages/Announcements'));
const Budgets           = lazy(() => import('./pages/Budgets'));
const ShortTermRentals  = lazy(() => import('./pages/ShortTermRentals'));
const AlertRules        = lazy(() => import('./pages/AlertRules'));
const Billing           = lazy(() => import('./pages/Billing'));
const Setup             = lazy(() => import('./pages/Setup'));

// ── Role constants ─────────────────────────────────────────────────────────────
// Landlord-portal route guard. Superadmin EXCLUDED — the platform operator has a
// separate platform-only shell (see navigation.js) and is bounced to /platform by
// ProtectedRoute if they hit a landlord route directly.
const OWNER_ROLES  = ['owner', 'manager'];
const ALL_ROLES    = ['superadmin', 'owner', 'manager', 'tenant', 'maintenance', 'caretaker'];

// Keeps the :id when an old superadmin detail path redirects into /platform/.
function RedirectWithId({ to }) {
  const { id } = useParams();
  return <Navigate to={`${to}/${id}`} replace />;
}

function RoleBasedRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user)   return <Navigate to="/login" replace />;
  return <Navigate to={getRoleHomePath(user.role)} replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user)    return <Navigate to={getRoleHomePath(user.role)} replace />;
  return children;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries:   { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password"  element={<PublicRoute><ResetPassword /></PublicRoute>} />
              <Route path="/verify-email"    element={<PublicRoute><VerifyEmail /></PublicRoute>} />
              <Route path="/accept-invite"   element={<PublicRoute><AcceptInvite /></PublicRoute>} />
              <Route path="/request-access/verify" element={<PublicRoute><RequestAccessVerify /></PublicRoute>} />
              <Route path="/request-access"  element={<PublicRoute><RequestAccess /></PublicRoute>} />

              {/* Public marketplace — deliberately NO PublicRoute wrapper:
                  that wrapper bounces logged-in users to their dashboard,
                  but an authed user may browse listings too. Top-level on
                  purpose — anything nested under "/" is swallowed by the
                  ProtectedRoute layout and bounces anon visitors to /login. */}
              <Route path="/listings" element={<Listings />} />
              <Route path="/listings/:id" element={<ListingDetail />} />
              <Route path="/applications/verify" element={<ApplicationVerify />} />
              {/* v1.4 TH-2: the public check of a Rent Payment Record (the QR on the PDF). */}
              <Route path="/verify/:token" element={<VerifyRecord />} />

              <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<RoleBasedRedirect />} />

                {/* ── Owner / Manager / Superadmin ── */}
                <Route path="dashboard"      element={<ProtectedRoute roles={OWNER_ROLES}><Dashboard /></ProtectedRoute>} />
                <Route path="properties"     element={<ProtectedRoute roles={OWNER_ROLES}><Properties /></ProtectedRoute>} />
                <Route path="properties/:id" element={<ProtectedRoute roles={OWNER_ROLES}><PropertyDetail /></ProtectedRoute>} />
                <Route path="units"          element={<ProtectedRoute roles={OWNER_ROLES}><Units /></ProtectedRoute>} />
                <Route path="units/:id"      element={<ProtectedRoute roles={OWNER_ROLES}><UnitDetail /></ProtectedRoute>} />
                <Route path="tenants"        element={<ProtectedRoute roles={OWNER_ROLES}><Tenants /></ProtectedRoute>} />
                <Route path="tenants/:id"    element={<ProtectedRoute roles={OWNER_ROLES}><TenantDetail /></ProtectedRoute>} />
                <Route path="staff"          element={<ProtectedRoute roles={['owner']}><Staff /></ProtectedRoute>} />
                <Route path="staff/:id"      element={<ProtectedRoute roles={['owner']}><StaffDetail /></ProtectedRoute>} />
                <Route path="import"         element={<ProtectedRoute roles={['owner', 'manager']}><ImportData /></ProtectedRoute>} />
                <Route path="leases"         element={<ProtectedRoute roles={OWNER_ROLES}><Leases /></ProtectedRoute>} />
                <Route path="leases/:id"     element={<ProtectedRoute roles={OWNER_ROLES}><LeaseDetail /></ProtectedRoute>} />
                <Route path="applications"   element={<ProtectedRoute roles={OWNER_ROLES}><Applications /></ProtectedRoute>} />
                <Route path="applications/:id" element={<ProtectedRoute roles={OWNER_ROLES}><ApplicationDetail /></ProtectedRoute>} />
                <Route path="invoices"       element={<ProtectedRoute roles={OWNER_ROLES}><Invoices /></ProtectedRoute>} />
                <Route path="payments"       element={<ProtectedRoute roles={OWNER_ROLES}><Payments /></ProtectedRoute>} />
                <Route path="mpesa-logs"     element={<ProtectedRoute roles={OWNER_ROLES}><MpesaLogs /></ProtectedRoute>} />
                <Route path="analytics"      element={<ProtectedRoute roles={OWNER_ROLES}><Analytics /></ProtectedRoute>} />
                {/* KRA-1: the monthly rental-income report (manager needs the financial-reports flag; the page shows the 403) */}
                <Route path="reports/rental-income" element={<ProtectedRoute roles={OWNER_ROLES}><RentalIncomeReport /></ProtectedRoute>} />
                <Route path="visitors" element={<ProtectedRoute roles={[...OWNER_ROLES, 'caretaker']}><VisitorLog /></ProtectedRoute>} />
                <Route path="service-charge/:id/statement" element={<ProtectedRoute roles={[...OWNER_ROLES, 'tenant']}><ServiceChargeStatement /></ProtectedRoute>} />
                <Route path="expenses"       element={<ProtectedRoute roles={OWNER_ROLES}><Expenses /></ProtectedRoute>} />
                <Route path="vendors"        element={<ProtectedRoute roles={OWNER_ROLES}><Vendors /></ProtectedRoute>} />
                <Route path="inspections"    element={<ProtectedRoute roles={OWNER_ROLES}><Inspections /></ProtectedRoute>} />
                <Route path="collections"    element={<ProtectedRoute roles={OWNER_ROLES}><Collections /></ProtectedRoute>} />
                <Route path="deposits"       element={<ProtectedRoute roles={OWNER_ROLES}><Deposits /></ProtectedRoute>} />
                <Route path="utilities"      element={<ProtectedRoute roles={OWNER_ROLES}><Utilities /></ProtectedRoute>} />
                {/* Owner ONLY (not managers) — the landlord's own LPMS bill (2H) */}
                <Route path="billing"        element={<ProtectedRoute roles={['owner']}><Billing /></ProtectedRoute>} />
                {/* N-5: owner setup wizard — offered from the Dashboard banner, never forced. */}
                <Route path="setup"          element={<ProtectedRoute roles={['owner']}><Setup /></ProtectedRoute>} />

                {/* ── New pages ── */}
                <Route path="vacate-notices"   element={<ProtectedRoute roles={OWNER_ROLES}><VacateNotices /></ProtectedRoute>} />
                <Route path="vacancy-listings" element={<ProtectedRoute roles={OWNER_ROLES}><VacancyListings /></ProtectedRoute>} />
                <Route path="enquiries"        element={<ProtectedRoute roles={OWNER_ROLES}><Enquiries /></ProtectedRoute>} />
                <Route path="enquiries/:id"    element={<ProtectedRoute roles={OWNER_ROLES}><EnquiryDetail /></ProtectedRoute>} />
                <Route path="announcements"    element={<ProtectedRoute roles={OWNER_ROLES}><Announcements /></ProtectedRoute>} />
                <Route path="budgets"          element={<ProtectedRoute roles={OWNER_ROLES}><Budgets /></ProtectedRoute>} />
                <Route path="short-term"       element={<ProtectedRoute roles={OWNER_ROLES}><ShortTermRentals /></ProtectedRoute>} />
                <Route path="alert-rules"      element={<ProtectedRoute roles={OWNER_ROLES}><AlertRules /></ProtectedRoute>} />

                {/* ── Superadmin only ── */}
                {/* ── Platform-operator console (superadmin) ── */}
                <Route path="platform"                    element={<ProtectedRoute roles={['superadmin']}><PlatformDashboard /></ProtectedRoute>} />
                <Route path="platform/organisations"      element={<ProtectedRoute roles={['superadmin']}><Organisations /></ProtectedRoute>} />
                <Route path="platform/organisations/:id"  element={<ProtectedRoute roles={['superadmin']}><OrganisationDetail /></ProtectedRoute>} />
                <Route path="platform/revenue"            element={<ProtectedRoute roles={['superadmin']}><PlatformRevenue /></ProtectedRoute>} />
                <Route path="platform/payments"           element={<ProtectedRoute roles={['superadmin']}><PaymentsHealth /></ProtectedRoute>} />
                <Route path="platform/system"             element={<ProtectedRoute roles={['superadmin']}><PlatformSystem /></ProtectedRoute>} />
                <Route path="platform/audit"              element={<ProtectedRoute roles={['superadmin']}><PlatformAudit /></ProtectedRoute>} />
                <Route path="platform/compliance"         element={<ProtectedRoute roles={['superadmin']}><ComplianceConfig /></ProtectedRoute>} />
                <Route path="platform/plans"              element={<ProtectedRoute roles={['superadmin']}><Plans /></ProtectedRoute>} />
                <Route path="platform/prospects"          element={<ProtectedRoute roles={['superadmin']}><Prospects /></ProtectedRoute>} />
                <Route path="platform/users"              element={<ProtectedRoute roles={['superadmin']}><UserManagement /></ProtectedRoute>} />
                <Route path="platform/users/:id"          element={<ProtectedRoute roles={['superadmin']}><PlatformUserDetail /></ProtectedRoute>} />
                {/* Old superadmin paths → the console (bookmarks, mobile deep links) */}
                <Route path="users"          element={<Navigate to="/platform/users" replace />} />
                <Route path="users/:id"      element={<RedirectWithId to="/platform/users" />} />
                <Route path="audit-logs"     element={<Navigate to="/platform/audit" replace />} />
                <Route path="prospects"      element={<Navigate to="/platform/prospects" replace />} />
                <Route path="accounts"       element={<Navigate to="/platform/organisations" replace />} />
                <Route path="accounts/:id"   element={<RedirectWithId to="/platform/organisations" />} />

                {/* ── Maintenance staff + owners ── */}
                <Route path="maintenance"    element={<ProtectedRoute roles={['owner','manager','maintenance']}><Maintenance /></ProtectedRoute>} />

                {/* ── All authenticated users ── */}
                <Route path="messages"       element={<ProtectedRoute roles={['owner','manager','tenant','maintenance']}><Messages /></ProtectedRoute>} />
                <Route path="settings"       element={<ProtectedRoute roles={ALL_ROLES}><Settings /></ProtectedRoute>} />

                {/* ── Tenant only ── */}
                <Route path="home"             element={<ProtectedRoute roles={['tenant']}><Home /></ProtectedRoute>} />
                <Route path="notices"          element={<ProtectedRoute roles={['tenant']}><TenantNotices /></ProtectedRoute>} />
                <Route path="my-deposit"       element={<ProtectedRoute roles={['tenant']}><MyDeposit /></ProtectedRoute>} />
                <Route path="my-visitors"      element={<ProtectedRoute roles={['tenant']}><MyVisitors /></ProtectedRoute>} />
                <Route path="rent-records"     element={<ProtectedRoute roles={['tenant']}><RentRecords /></ProtectedRoute>} />
                <Route path="my-lease"         element={<ProtectedRoute roles={['tenant']}><MyLease /></ProtectedRoute>} />
                <Route path="payment-history"  element={<ProtectedRoute roles={['tenant']}><PaymentHistory /></ProtectedRoute>} />
                <Route path="receipts"         element={<ProtectedRoute roles={['tenant']}><Receipts /></ProtectedRoute>} />
                <Route path="report-issue"     element={<ProtectedRoute roles={['tenant']}><ReportIssue /></ProtectedRoute>} />
                <Route path="my-maintenance"   element={<ProtectedRoute roles={['tenant']}><MyMaintenance /></ProtectedRoute>} />
                <Route path="my-maintenance/:id" element={<ProtectedRoute roles={['tenant']}><MyMaintenanceDetail /></ProtectedRoute>} />
                <Route path="notice-to-vacate" element={<ProtectedRoute roles={['tenant']}><NoticeToVacate /></ProtectedRoute>} />

                {/* ── Maintenance staff only ── */}
                <Route path="my-tasks"         element={<ProtectedRoute roles={['maintenance']}><MyTasks /></ProtectedRoute>} />

                <Route path="*" element={<RoleBasedRedirect />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
